import { DateRange, Trip } from '@types'

/**
 * TripHelper class provides utility methods for trip-related operations
 * Consolidates duplicate date formatting, parsing, and calculation logic
 */
export class TripHelper {
	private readonly trip: Trip

	constructor(trip: Trip) {
		this.trip = trip
	}

	/**
	 * Formats a date string from YYYYMMDD format to a human-readable format
	 * @param dateString - Date in YYYYMMDD format (can be null)
	 * @returns Formatted date string or fallback text
	 */
	static formatDate(dateString: string | null): string {
		if (!dateString) return 'No dates set'

		try {
			// Assuming dateString is in YYYYMMDD format
			const year = dateString.substring(0, 4)
			const month = dateString.substring(4, 6)
			const day = dateString.substring(6, 8)
			const date = new Date(Number(year), Number(month) - 1, Number(day))

			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			})
		} catch {
			return 'Invalid date'
		}
	}

	/**
	 * Parses YYYYMMDD format date string to a Date object
	 * @param dateString - Date in YYYYMMDD format
	 * @returns Date object or null if invalid
	 */
	static parseDate(dateString: string): Date | null {
		if (!dateString || dateString.length !== 8) return null

		try {
			const year = Number(dateString.substring(0, 4))
			const month = Number(dateString.substring(4, 6)) - 1
			const day = Number(dateString.substring(6, 8))
			return new Date(year, month, day)
		} catch {
			return null
		}
	}

	private static formatDateToString(date: Date): string {
		const y = date.getFullYear()
		const m = String(date.getMonth() + 1).padStart(2, '0')
		const d = String(date.getDate()).padStart(2, '0')
		return `${y}-${m}-${d}`
	}

	/**
	 * Gets the trip start date as a Date object
	 * @returns Date object or null if startDate is not available
	 */
	getStartDate(): Date | null {
		return TripHelper.parseDate(this.trip.startDate || '')
	}

	/**
	 * Gets the trip end date as a Date object
	 * @returns Date object or null if startDate or dayCount is not available
	 */
	getEndDate(): Date | null {
		const startDate = this.getStartDate()
		if (!startDate || !this.trip.dayCount) return null

		const endDate = new Date(startDate)
		endDate.setDate(startDate.getDate() + this.trip.dayCount - 1)
		return endDate
	}

	/**
	 * Gets the trip start date formatted for display
	 * @returns Formatted date string
	 */
	getFormattedStartDate(): string {
		return TripHelper.formatDate(this.trip.startDate)
	}

	/**
	 * Gets the trip end date formatted for display
	 * @returns Formatted date string
	 */
	getFormattedEndDate(): string {
		const endDate = this.getEndDate()
		if (!endDate) return 'No dates set'

		return endDate.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		})
	}

	/**
	 * Calculates the actual date from trip start date and tripDayIndex
	 * @param tripDayIndex - 0-based day index
	 * @returns Date string in YYYY-MM-DD format
	 */
	calculateDateFromDayIndex(tripDayIndex: number): string {
		const startDate = this.getStartDate()
		if (!startDate) return ''

		try {
			const eventDateObj = new Date(startDate)
			eventDateObj.setDate(startDate.getDate() + tripDayIndex)
			return TripHelper.formatDateToString(eventDateObj)
		} catch (error) {
			console.error('Error calculating date from day index:', error)
			return ''
		}
	}

	/**
	 * Calculates the tripDayIndex from a date string and trip start date
	 * @param eventDate - Date string in YYYY-MM-DD format
	 * @returns tripDayIndex (0-based day index)
	 */
	calculateDayIndexFromDate(eventDate: string): number {
		const startDate = this.getStartDate()
		if (!startDate) return 0

		try {
			const eventDateObj = new Date(eventDate)
			return Math.floor((eventDateObj.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
		} catch (error) {
			console.error('Error calculating day index from date:', error)
			return 0
		}
	}

	/**
	 * Gets the trip duration in days
	 * @returns Number of days or 0 if not available
	 */
	getDuration(): number {
		return this.trip.dayCount || 0
	}

	/**
	 * Checks if the trip has valid date information
	 * @returns true if trip has startDate and dayCount
	 */
	hasValidDates(): boolean {
		return !!(this.trip.startDate && this.trip.dayCount && this.trip.dayCount > 0)
	}

	/**
	 * Gets the trip date range as ISO date strings
	 * @returns DateRange object with startDate and endDate in YYYY-MM-DD format
	 */
	getDateRange(): DateRange | null {
		const startDate = this.getStartDate()
		const endDate = this.getEndDate()

		if (!startDate || !endDate) return null

		return {
			startDate: TripHelper.formatDateToString(startDate),
			endDate: TripHelper.formatDateToString(endDate),
		}
	}

	/**
	 * Generates an array of day objects for the trip
	 * @returns Array of day objects with id, date, and displayName
	 */
	generateDays(): Array<{ id: string; date: string; displayName: string }> {
		const startDate = this.getStartDate()
		const endDate = this.getEndDate()

		if (!startDate || !endDate) return []

		const days: Array<{ id: string; date: string; displayName: string }> = []

		for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
			const dateString = TripHelper.formatDateToString(d)
			const dayId = `day-${dateString}`
			const displayName = d.toLocaleDateString('en-US', { weekday: 'long' })
			days.push({ id: dayId, date: dateString, displayName })
		}

		return days
	}

	/**
	 * Extracts date string from dayId
	 * @param dayId - Day ID in format "day-YYYY-MM-DD"
	 * @returns Date string in YYYY-MM-DD format
	 */
	static extractDateFromDayId(dayId: string): string {
		return dayId.replace('day-', '')
	}

	/**
	 * Converts trip date from YYYYMMDD format to YYYY-MM-DD format for HTML date inputs
	 * @param tripDate - Date in YYYYMMDD format (can be null)
	 * @returns Date in YYYY-MM-DD format or empty string if invalid
	 */
	static convertTripDateToInputFormat(tripDate: string | null): string {
		if (!tripDate || tripDate.length !== 8) return ''
		const year = tripDate.substring(0, 4)
		const month = tripDate.substring(4, 6)
		const day = tripDate.substring(6, 8)
		return `${year}-${month}-${day}`
	}
}

// Export static methods for backward compatibility
export const formatTripDate = TripHelper.formatDate
export const parseTripDate = TripHelper.parseDate
export const extractDateFromDayId = TripHelper.extractDateFromDayId
export const convertTripDateToInputFormat = TripHelper.convertTripDateToInputFormat
